import { edgeMap } from "./homeSort.js";
import { decisionDeadline } from "./marketAge.js";
import { isMorningGame, passesMorningRule } from "./morningGame.js";

// Home's "pick the top 5" (the round 5 button), and what keeps those picks on
// the top five as the lines move between the morning and the deadline.
//
// Picks carry a `source`: "app" when this planner made them, "hand" when the
// picker tapped a side. Only app picks are ever swapped. A hand pick, a pick on
// the commissioner's sheet and a game past its decision deadline are fixed:
// they hold their slot, and the planner fills or re-checks only the rest.
//
// Ranking is on `winProbability` and nothing else (CLAUDE.md: win probability
// is expected pool points), on each game's edge side.

// A swap has to buy at least this much outright win probability, in
// percentage points (edge.js probabilities are percents). Below it the
// "change" is the books' noise, and every swap after an email means sending
// another one.
export const MIN_SWAP_GAIN = 0.5;

export const TOP_FIVE = 5;

function isOpen(game, now) {
  if (game.completed) return false;
  const deadline = decisionDeadline(game);
  return deadline == null || now < deadline;
}

// The chance a given side of a game wins outright, read off its edge.
function winOf(edge, side) {
  return side === edge.side ? edge.winProbability : edge.lossProbability;
}

// The picks this planner may move: made by the app, still open, not on the
// sheet, and with a market to judge them by.
function movable(game, edge, now) {
  const pick = game.pick;
  return (
    pick?.source === "app" &&
    !pick.submitted_at &&
    isOpen(game, now) &&
    edge.measurable
  );
}

// Following the top five: at least one app pick the planner can still move.
export function isFollowing(games, now = Date.now()) {
  return games.some(
    (game) => game.pick?.source === "app" && !game.pick.submitted_at && isOpen(game, now)
  );
}

/**
 * What to change to put the app's slots on the current top five.
 *
 * mode "start": the 5 button was tapped -- fill every free slot and swap.
 * mode "follow": the lines refreshed -- swap only; never add a pick the
 *   picker may have just removed by hand.
 *
 * Returns `{ adds, swaps }`. `adds` is `[{ game, side, win }]`; `swaps` is
 * `[{ out: { game, side, win }, in: { game, side, win }, gain }]`, where a
 * side flip on the same game is a swap whose `out.game === in.game`.
 */
export function planTopFive(games, {
  edges = edgeMap(games),
  now = Date.now(),
  isRanked = () => true,
  mode = "follow",
  minGain = MIN_SWAP_GAIN,
} = {}) {
  const edgeOf = (game) => edges.get(game);
  const picked = games.filter((game) => game.pick);
  const appPicks = picked.filter((game) => movable(game, edgeOf(game), now));
  const fixed = picked.filter((game) => !appPicks.includes(game));
  const slots = Math.max(0, TOP_FIVE - fixed.length);

  const ranked = (game) => {
    const edge = edgeOf(game);
    return (
      isRanked(game) &&
      isOpen(game, now) &&
      edge.measurable &&
      passesMorningRule(game.kickoffAt, edge.winProbability)
    );
  };
  const morning = (game) => isMorningGame(game.kickoffAt);

  // The best games for the app's slots: ranked, open, priced, not already
  // held by a fixed pick. A Sunday-morning game under MORNING_GAME_BAR is
  // never one (it would most likely be bumped by 1pm, and it locks first);
  // one over the bar takes its slot ahead of the later games, the same order
  // the You vs App board uses.
  const target = games
    .filter((game) => !fixed.includes(game) && ranked(game))
    .sort((a, b) =>
      (morning(b) - morning(a)) ||
      (edgeOf(b).winProbability - edgeOf(a).winProbability)
    )
    .slice(0, slots);

  const entry = (game, side) => ({ game, side, win: winOf(edgeOf(game), side) });
  const swaps = [];

  // An app pick whose game is still top five but whose better side flipped.
  for (const game of appPicks.filter((g) => target.includes(g))) {
    const edge = edgeOf(game);
    if (game.pick.side === edge.side) continue;
    const out = entry(game, game.pick.side);
    const into = entry(game, edge.side);
    if (into.win - out.win >= minGain) swaps.push({ out, in: into, gain: into.win - out.win });
  }

  // App picks that fell out of the top five, worst first, against the top
  // games nobody holds, best first. Each pair swaps only if it gains enough;
  // the first pair that does not ends it, since every later pair gains less.
  const drops = appPicks
    .filter((game) => !target.includes(game))
    .map((game) => entry(game, game.pick.side))
    .sort((a, b) => a.win - b.win);
  // Sorted on win alone: `target` puts a morning game first, and the early
  // stop below relies on each pair gaining no more than the one before.
  const free = target
    .filter((game) => !game.pick)
    .map((game) => entry(game, edgeOf(game).side))
    .sort((a, b) => b.win - a.win);

  let i = 0;
  for (; i < Math.min(drops.length, free.length); i += 1) {
    const gain = free[i].win - drops[i].win;
    if (gain < minGain) break;
    swaps.push({ out: drops[i], in: free[i], gain });
  }

  const adds = [];
  if (mode === "start") {
    const room = slots - appPicks.length;
    adds.push(...free.slice(i, i + Math.max(0, room)));
  }

  return { adds, swaps };
}
